/**
 * Stage 5.5 — AI Auditor (in-project section)
 *
 * Mounts inside ProjectView.js right below "5. Keyframe Gallery". UX clone of
 * the Stage 5 keyframe grid; the only visual diff is a row of audit chips
 * below each thumbnail + a per-card "Audit" / "Fix it" action button.
 *
 * Card click opens the existing GeminiIterationModal (the IGK SAM workbench)
 * so the operator can review pre-vs-post-autofix versions identically to how
 * IGK already works.
 *
 * Reference: /app/memory/stage_5_5_brief.md
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "../utils/toast";
import {
  ChevronDown,
  Loader2,
  Play,
  Search,
  ShieldCheck,
  Sparkles,
  Wrench,
} from "lucide-react";
import GeminiIterationModal from "./GeminiIterationModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PASS_1_AXES = [
  { key: "GESTURE_PRESERVED", short: "Gesture" },
  { key: "EMOTION_ALIGNMENT", short: "Emotion" },
  { key: "DOP_FIDELITY", short: "DOP" },
  { key: "NARRATIVE_BEAT_PRESERVED", short: "Beat" },
  { key: "VISIBILITY_MODE_ENFORCED", short: "Visibility" },
];

const BATCH_OPTIONS = [
  { value: 3, label: "3 at a time" },
  { value: 5, label: "5 at a time" },
  { value: 10, label: "10 at a time" },
  { value: 28, label: "All shots" },
];

function chipColor(status) {
  if (status === "clean") return { bg: "#22C55E20", fg: "#22C55E", border: "#22C55E40" };
  if (status === "minor") return { bg: "#FF950020", fg: "#FF9500", border: "#FF950040" };
  if (status === "major") return { bg: "#FF453A20", fg: "#FF453A", border: "#FF453A40" };
  if (status === "not_evaluable") return { bg: "#8E8E9320", fg: "#8E8E93", border: "#8E8E9340" };
  return { bg: "#1C1C1E", fg: "#8E8E93", border: "#2A2A2E" };
}

function chipIcon(status) {
  if (status === "clean") return "✓";
  if (status === "minor") return "·";
  if (status === "major") return "!";
  return "?";
}

export default function Stage55Section({ projectId, project, shots, onShotsRefresh, onSkip }) {
  const [batchSize, setBatchSize] = useState(3);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ completed: 0, total: 0 });
  const [autofixRunning, setAutofixRunning] = useState(false);
  const [autofixProgress, setAutofixProgress] = useState({ completed: 0, total: 0, succeeded: 0, failed: 0 });
  const [perShotLoading, setPerShotLoading] = useState({}); // {shotId: 'auditing'|'fixing'}
  const [modalShot, setModalShot] = useState(null); // shot doc to open in IGK SAM modal

  // Hydrate batch state from project
  useEffect(() => {
    setBatchRunning(!!project?.stage_5_5_processing);
    setBatchProgress({
      completed: project?.stage_5_5_pass_1_completed || 0,
      total: project?.stage_5_5_pass_1_total || 0,
    });
    setAutofixRunning(!!project?.stage_5_5_autofix_processing);
    setAutofixProgress({
      completed: project?.stage_5_5_autofix_completed || 0,
      total: project?.stage_5_5_autofix_total || 0,
      succeeded: project?.stage_5_5_autofix_succeeded || 0,
      failed: project?.stage_5_5_autofix_failed || 0,
    });
  }, [
    project?.stage_5_5_processing,
    project?.stage_5_5_pass_1_completed,
    project?.stage_5_5_pass_1_total,
    project?.stage_5_5_autofix_processing,
    project?.stage_5_5_autofix_completed,
    project?.stage_5_5_autofix_total,
    project?.stage_5_5_autofix_succeeded,
    project?.stage_5_5_autofix_failed,
  ]);

  // Poll status while running
  useEffect(() => {
    if (!batchRunning && !autofixRunning) return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}/stage-5-5/status`);
        if (batchRunning) {
          setBatchProgress({
            completed: r.data.pass_1_completed,
            total: r.data.pass_1_total,
          });
          if (!r.data.processing) {
            setBatchRunning(false);
            if (onShotsRefresh) await onShotsRefresh();
            toast.success(`AI Auditor complete — ${r.data.pass_1_completed}/${r.data.pass_1_total}`);
          }
        }
        if (autofixRunning) {
          setAutofixProgress({
            completed: r.data.autofix_completed,
            total: r.data.autofix_total,
            succeeded: r.data.autofix_succeeded,
            failed: r.data.autofix_failed,
          });
          if (!r.data.autofix_processing) {
            setAutofixRunning(false);
            if (onShotsRefresh) await onShotsRefresh();
            const ok = r.data.autofix_succeeded;
            const fail = r.data.autofix_failed;
            if (fail === 0) {
              toast.success(`Fix All complete — all ${ok} shot(s) fixed`);
            } else {
              toast.warning(`Fix All done — ${ok} fixed, ${fail} skipped (open shot for details)`);
            }
          }
        }
      } catch { /* polling errors swallowed */ }
    }, 4000);
    return () => clearInterval(t);
  }, [batchRunning, autofixRunning, projectId, onShotsRefresh]);

  // Engine-agnostic eligibility (2026-05-28): any shot with an active
  // `keyframe_url` qualifies, regardless of which engine produced it
  // (MJ, Gemini IGK, Keyframe+/gpt-image-2, or any future pipeline).
  const eligible = (shots || []).filter(
    (s) => s.keyframe_url
  );
  const auditedCount = eligible.filter((s) => s.stage_5_5_audit_latest && !s.stage_5_5_audit_latest?.error).length;
  const flaggedCount = eligible.filter((s) => {
    const a = s.stage_5_5_audit_latest;
    return a && !a.error && (a.total_major_flags > 0 || a.total_minor_flags > 0);
  }).length;
  // Shots eligible for "Fix All": flagged AND not already fixed since the last audit
  const fixableCount = useMemo(() => eligible.filter((s) => {
    const a = s.stage_5_5_audit_latest;
    if (!a || a.error) return false;
    if (!((a.total_major_flags || 0) > 0 || (a.total_minor_flags || 0) > 0)) return false;
    const fixAt = s.stage_5_5_autofix_at;
    const auditAt = a.audited_at;
    if (fixAt && auditAt && fixAt > auditAt) return false; // already fixed since this audit
    return true;
  }).length, [eligible]);

  const runFullAudit = useCallback(async () => {
    try {
      setBatchRunning(true);
      const r = await axios.post(
        `${API}/projects/${projectId}/stage-5-5/start-batch`,
        { pass_mode: "pass_1_only", batch_size: batchSize },
      );
      toast.info(r.data.message || "AI Auditor started");
    } catch (e) {
      setBatchRunning(false);
      toast.error(e?.response?.data?.detail || "Failed to start AI Auditor");
    }
  }, [batchSize, projectId]);

  const runFixAll = useCallback(async () => {
    if (fixableCount === 0) return;
    if (!window.confirm(`Run GPT-Image-2 auto-fix on ${fixableCount} flagged shot(s)? This may take ${Math.ceil(fixableCount * 1.5)}-${fixableCount * 3} minutes.`)) return;
    try {
      setAutofixRunning(true);
      const r = await axios.post(`${API}/projects/${projectId}/stage-5-5/autofix-all`);
      toast.info(r.data.message || "Fix All started");
    } catch (e) {
      setAutofixRunning(false);
      toast.error(e?.response?.data?.detail || "Failed to start Fix All");
    }
  }, [projectId, fixableCount]);

  const auditOneShot = useCallback(async (shotId) => {
    setPerShotLoading((s) => ({ ...s, [shotId]: "auditing" }));
    try {
      await axios.post(`${API}/projects/${projectId}/stage-5-5/audit-shot/${shotId}`);
      if (onShotsRefresh) await onShotsRefresh();
      toast.success("Shot audited");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally {
      setPerShotLoading((s) => ({ ...s, [shotId]: null }));
    }
  }, [projectId, onShotsRefresh]);

  const autofixOneShot = useCallback(async (shotId) => {
    setPerShotLoading((s) => ({ ...s, [shotId]: "fixing" }));
    try {
      const r = await axios.post(`${API}/projects/${projectId}/stage-5-5/autofix/${shotId}`);
      if (onShotsRefresh) await onShotsRefresh();
      toast.success(r.data.message || "Auto-fix complete — open workbench to review");
      // Open the SAM modal so operator can pick original vs corrected
      const refreshedShot = shots.find((sh) => sh.id === shotId);
      if (refreshedShot) setModalShot(refreshedShot);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fix failed");
    } finally {
      setPerShotLoading((s) => ({ ...s, [shotId]: null }));
    }
  }, [projectId, shots, onShotsRefresh]);

  const promoteKPlus = useCallback(async (shotId) => {
    // Calls the existing K+ promote endpoint. The auto-promote rules in
    // routes/keyframe_plus.py deliberately hold V2+ K+ outputs pending
    // operator approval to protect a working IGK/MJ keyframe; this UI
    // is the operator's explicit approval. After promotion the shot's
    // keyframe_url + keyframe_engine flip to the K+ output.
    setPerShotLoading((s) => ({ ...s, [shotId]: "promoting" }));
    try {
      const r = await axios.post(
        `${API}/shots/${shotId}/keyframe-plus/promote`,
      );
      toast.success("K+ version promoted as active keyframe");
      if (onShotsRefresh) await onShotsRefresh();
      return r.data;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail
        : detail?.message || e.message || "K+ promote failed");
    } finally {
      setPerShotLoading((s) => ({ ...s, [shotId]: null }));
    }
  }, [onShotsRefresh]);

  const approveAndLock = useCallback(async () => {
    if (!window.confirm("Approve all and lock keyframes? This advances the project.")) return;
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/stage-5-5/approve-and-lock`,
        { soft: true },
      );
      if (r.data.open_major_flags > 0) {
        if (!window.confirm(`${r.data.open_major_flags} MAJOR audit flag(s) still open. Advance anyway?`)) return;
      }
      toast.success("Approved & locked");
      if (onShotsRefresh) await onShotsRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  }, [projectId, onShotsRefresh]);

  if (eligible.length === 0) {
    return (
      <div className="text-center py-8 text-xs text-white/50" data-testid="stage-5-5-empty">
        No locked keyframes yet — generate keyframes at Section 5 first.
      </div>
    );
  }

  return (
    <div data-testid="stage-5-5-section">
      {/* Control strip */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="relative">
          <select
            value={batchSize}
            onChange={(e) => setBatchSize(Number(e.target.value))}
            disabled={batchRunning}
            data-testid="stage-5-5-batch-size"
            className="appearance-none bg-[#1C1C1E] border border-white/10 rounded-lg px-3 py-1.5 pr-8 text-xs hover:border-[#0EA5E9] transition"
          >
            {BATCH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
        </div>

        <button
          onClick={runFullAudit}
          disabled={batchRunning}
          data-testid="run-ai-auditor-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0EA5E9] hover:bg-[#0284C7] disabled:bg-white/5 disabled:text-white/30 text-white text-xs font-medium transition"
        >
          {batchRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {batchRunning ? "Auditing..." : "Run AI Auditor"}
        </button>

        <button
          onClick={runFixAll}
          disabled={autofixRunning || batchRunning || fixableCount === 0}
          data-testid="run-fix-all-btn"
          title={fixableCount === 0 ? "No flagged shots needing fixes" : `Auto-fix ${fixableCount} flagged shot(s) with GPT-Image-2`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF9500] hover:bg-[#E08600] disabled:bg-white/5 disabled:text-white/30 text-white text-xs font-medium transition"
        >
          {autofixRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />}
          {autofixRunning ? "Fixing..." : `Fix All${fixableCount > 0 ? ` (${fixableCount})` : ""}`}
        </button>

        {batchRunning && (
          <div className="flex items-center gap-2 text-xs text-white/60" data-testid="stage-5-5-progress">
            <div className="w-32 h-1 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#0EA5E9] transition-all"
                style={{ width: `${batchProgress.total ? (100 * batchProgress.completed / batchProgress.total) : 0}%` }} />
            </div>
            <span>{batchProgress.completed} / {batchProgress.total}</span>
          </div>
        )}

        {autofixRunning && (
          <div className="flex items-center gap-2 text-xs text-white/60" data-testid="stage-5-5-autofix-progress">
            <div className="w-32 h-1 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#FF9500] transition-all"
                style={{ width: `${autofixProgress.total ? (100 * autofixProgress.completed / autofixProgress.total) : 0}%` }} />
            </div>
            <span>
              {autofixProgress.completed} / {autofixProgress.total}
              {autofixProgress.failed > 0 && <span className="text-[#FF453A]"> · {autofixProgress.failed} failed</span>}
            </span>
          </div>
        )}

        <div className="ml-auto flex items-center gap-3 text-xs text-white/60">
          <span><span className="text-[#22C55E] font-medium">{auditedCount}</span> audited</span>
          {flaggedCount > 0 && <span><span className="text-[#FF9500] font-medium">{flaggedCount}</span> flagged</span>}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {eligible.map((s) => (
          <Stage55Card
            key={s.id}
            shot={s}
            onAudit={() => auditOneShot(s.id)}
            onAutofix={() => autofixOneShot(s.id)}
            onOpenModal={() => setModalShot(s)}
            onPromoteKPlus={promoteKPlus}
            loading={perShotLoading[s.id]}
          />
        ))}
      </div>

      {/* Bottom CTA row — skip-or-lock. Stage 5.5 is OPTIONAL (operator
          may skip the AI audit entirely and move straight to the next
          stage). The skip button is always visible whenever the section
          renders; the lock button appears only after at least one shot
          has been audited. */}
      <div className="flex justify-end items-center gap-2 mt-4">
        {onSkip && (
          <button
            onClick={onSkip}
            data-testid="stage-5-5-skip-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white/90 text-xs font-medium active:scale-95 transition"
            title="Skip the AI audit step entirely and advance to the next stage. Use this when you've manually reviewed the keyframes in the workbench and don't want the AI auditor to re-audit them."
          >
            Skip audit, move to next →
          </button>
        )}
        {auditedCount > 0 && (
          <button
            onClick={approveAndLock}
            data-testid="stage-5-5-approve-lock-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#22C55E]/15 hover:bg-[#22C55E]/25 border border-[#22C55E]/40 text-[#22C55E] text-xs font-semibold active:scale-95 transition"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Approved & Lock →
          </button>
        )}
      </div>

      {modalShot && (
        <GeminiIterationModal
          shotId={modalShot.id}
          shot={modalShot}
          aspectClass="aspect-video"
          targetField="keyframe_hyperreal_url"
          onClose={() => setModalShot(null)}
          onSelect={async () => {
            setModalShot(null);
            if (onShotsRefresh) await onShotsRefresh();
          }}
        />
      )}
    </div>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────

function Stage55Card({ shot, onAudit, onAutofix, onOpenModal, onPromoteKPlus, loading }) {
  const sid = `${shot.scene_number}-${shot.shot_number}`;
  // Engine-agnostic: always read the active keyframe_url. The producing
  // pipeline (IGK / K+ / MJ) mirrors its output here, so this works
  // regardless of which engine generated it. Keep the hyperreal-only
  // fallback for the rare case keyframe_url isn't aliased yet.
  const url = shot.keyframe_url || shot.keyframe_hyperreal_url;
  const audit = shot.stage_5_5_audit_latest;
  const errored = audit?.error;
  const hasFlags = audit && !errored && (
    (audit.total_major_flags || 0) > 0 || (audit.total_minor_flags || 0) > 0
  );
  const hasVersions = (shot.keyframe_hyperreal_versions || []).length > 1;
  // K+ produced an output but it isn't the currently-displayed keyframe.
  // Surfaces a purple "K+" promote badge so the operator knows there's a
  // ready-to-promote alternate version that the auto-promote rules
  // (routes/keyframe_plus.py case-c) deliberately held back to protect
  // the existing IGK / MJ keyframe.
  const kPlusPending = (
    (shot.keyframe_plus_versions || []).length > 0
    && shot.keyframe_engine !== "gpt-image-2-keyframe-plus"
  );
  const wasFixed = !!shot.stage_5_5_autofix_at;
  const fixIsStale = wasFixed && audit?.audited_at && new Date(audit.audited_at) > new Date(shot.stage_5_5_autofix_at);
  const [openChip, setOpenChip] = useState(null); // axis key whose popover is open
  const [promoting, setPromoting] = useState(false);

  const handlePromote = async (e) => {
    e?.stopPropagation();
    if (promoting) return;
    const ok = window.confirm(
      `Promote the Keyframe+ version to the active keyframe for shot ${sid}? `
      + `The current ${shot.keyframe_engine || 'IGK'} keyframe stays in version history.`
    );
    if (!ok) return;
    setPromoting(true);
    try {
      await onPromoteKPlus(shot.id);
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div
      data-testid={`stage-5-5-card-${sid}`}
      className="bg-[#0F1521] border border-white/10 rounded-xl hover:border-[#0EA5E9]/40 transition flex flex-col"
    >
      <button
        type="button"
        onClick={onOpenModal}
        className="aspect-video bg-black relative block w-full text-left rounded-t-xl overflow-hidden"
        data-testid={`stage-5-5-thumb-${sid}`}
      >
        {url ? (
          <img src={url} alt={sid} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">no keyframe</div>
        )}
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-black/70 text-[10px] font-medium text-white">
          {sid}
        </div>
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {kPlusPending && (
            <button
              type="button"
              onClick={handlePromote}
              disabled={promoting}
              title="Keyframe+ has produced an alternate version that isn't shown. Click to promote it as the active keyframe (the current one stays in version history). Open the card to compare in the SAM workbench first if you want."
              className="px-2 py-0.5 rounded-md bg-[#A855F7] hover:bg-[#9333EA] text-[10px] font-bold text-white shadow-lg flex items-center gap-1 disabled:opacity-60 active:scale-95 transition"
              data-testid={`stage-5-5-kplus-promote-${sid}`}
            >
              {promoting
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Sparkles className="w-2.5 h-2.5" />}
              K+
            </button>
          )}
          {hasVersions && (
            <div
              className="px-2 py-0.5 rounded-md bg-[#0EA5E9]/80 text-[10px] font-medium text-white"
              data-testid={`stage-5-5-version-badge-${sid}`}
            >
              v{shot.keyframe_hyperreal_versions.length}
            </div>
          )}
        </div>
        {wasFixed && !fixIsStale && (
          <div
            data-testid={`stage-5-5-fixed-badge-${sid}`}
            className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-[#22C55E] text-[10px] font-bold text-white shadow-lg flex items-center gap-1"
          >
            <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
            </svg>
            Fixed
          </div>
        )}
      </button>

      <div className="p-3 space-y-2.5 flex-1 flex flex-col">
        <div className="flex flex-wrap gap-1 relative">
          {PASS_1_AXES.map((ax) => {
            const v = audit?.pass_1_axes?.[ax.key];
            const status = v?.status || "_unaudited";
            const c = chipColor(status);
            const overridden = v?.operator_override;
            const isOpen = openChip === ax.key;
            return (
              <button
                key={ax.key}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenChip(isOpen ? null : ax.key);
                }}
                data-testid={`stage-5-5-chip-${sid}-${ax.key}`}
                style={{
                  background: c.bg,
                  color: c.fg,
                  borderColor: c.border,
                  textDecoration: overridden ? "line-through" : undefined,
                  opacity: status === "_unaudited" ? 0.45 : 1,
                }}
                className="px-1.5 py-0.5 text-[9px] font-medium rounded border flex items-center gap-1 hover:brightness-125 active:scale-95 transition"
              >
                <span className="font-mono w-2 text-center">{v ? chipIcon(status) : "·"}</span>
                {ax.short}
              </button>
            );
          })}
          {openChip && audit?.pass_1_axes?.[openChip] && (
            <div
              data-testid={`stage-5-5-chip-popover-${sid}`}
              className="absolute top-full left-0 right-0 mt-1 p-2.5 rounded-lg bg-[#1C1C1E] border border-white/15 shadow-2xl z-30 text-[10px] leading-relaxed max-h-[60vh] overflow-y-auto overscroll-contain"
              onClick={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="font-mono font-semibold text-white">{openChip}</div>
                <button
                  onClick={(e) => { e.stopPropagation(); setOpenChip(null); }}
                  className="text-white/40 hover:text-white"
                  data-testid={`stage-5-5-chip-popover-close-${sid}`}
                >✕</button>
              </div>
              {(() => {
                const v = audit.pass_1_axes[openChip];
                const c2 = chipColor(v.status);
                return (
                  <>
                    <div className="flex gap-2 mb-1.5">
                      <span style={{ color: c2.fg }} className="font-semibold uppercase">{v.status}</span>
                      {typeof v.confidence === 'number' && (
                        <span className="text-white/40">conf {(v.confidence * 100).toFixed(0)}%</span>
                      )}
                    </div>
                    {v.complaint ? (
                      <div className="text-white/80 whitespace-pre-wrap">{v.complaint}</div>
                    ) : (
                      <div className="text-white/40 italic">No issue — this axis is clean.</div>
                    )}
                    {v.correction_prompt_seed && (
                      <details className="mt-2">
                        <summary className="text-white/40 cursor-pointer hover:text-white/70">show correction prompt</summary>
                        <div className="mt-1 text-white/60 whitespace-pre-wrap">{v.correction_prompt_seed}</div>
                      </details>
                    )}
                    {v.operator_override && (
                      <div className="mt-2 px-2 py-1 rounded bg-white/5 border border-white/10">
                        <span className="text-white/40">Operator override:</span>{" "}
                        <span className="text-white/80">{v.operator_override}</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        <div className="text-[10px] text-white/50">
          {errored ? (
            <span className="text-[#FF9500]">⚠ audit errored — re-run to retry</span>
          ) : audit ? (
            <>
              <span className="text-[#FF453A] font-medium">{audit.total_major_flags || 0}</span> major ·{" "}
              <span className="text-[#FF9500] font-medium">{audit.total_minor_flags || 0}</span> minor
              {audit.cost_usd ? <span className="text-white/30"> · ${audit.cost_usd.toFixed(2)}</span> : null}
              {wasFixed && !fixIsStale && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-[#22C55E]/15 border border-[#22C55E]/40 text-[#22C55E] font-semibold uppercase tracking-wide text-[9px]" data-testid={`stage-5-5-fixed-pill-${sid}`}>
                  ✓ Fixed
                </span>
              )}
              {wasFixed && fixIsStale && (
                <span className="ml-2 text-[#FF9500] italic">re-audit recommended</span>
              )}
            </>
          ) : (
            <span className="text-white/30">Not audited</span>
          )}
        </div>

        <div className="mt-auto flex items-center gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); onAudit(); }}
            disabled={!!loading}
            data-testid={`stage-5-5-audit-${sid}`}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 disabled:opacity-40 transition text-[10px] font-medium"
          >
            {loading === "auditing" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
            {audit ? "Re-audit" : "Audit"}
          </button>
          {hasFlags && (
            <button
              onClick={(e) => { e.stopPropagation(); onAutofix(); }}
              disabled={!!loading}
              data-testid={`stage-5-5-autofix-${sid}`}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-[#FF9500]/15 hover:bg-[#FF9500]/25 border border-[#FF9500]/40 text-[#FF9500] disabled:opacity-40 transition text-[10px] font-medium"
              title="Fire GPT-Image-2 to correct all flagged issues (MAJOR + MINOR), opens workbench for review"
            >
              {loading === "fixing" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />}
              Fix it
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Suppress unused-import: Play kept for future header redesign
const _unused = Play;
